import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { PRAYER_TYPES, DAY_TYPES } from '../../config/constants';
import { ScheduleEntry } from '../../types';
import {
  CsvParsedRow,
  CsvRowError,
  generateScheduleXlsxTemplate,
  mergeCsvRowsIntoSchedules,
  parseScheduleXlsx,
  scheduleMatchKey,
  ScheduleXlsxLabelSets,
} from '../../utils/scheduleXlsx';
import InfoTooltip from '../common/InfoTooltip';

interface ScheduleUploadProps {
  schedules: ScheduleEntry[];
  onUpload: (schedules: ScheduleEntry[]) => Promise<void> | void;
}

export default function ScheduleUpload({ schedules, onUpload }: ScheduleUploadProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const tEn = i18n.getFixedT('en');
  const tHe = i18n.getFixedT('he');
  const labelSets: ScheduleXlsxLabelSets = {
    prayers: PRAYER_TYPES.map((p) => ({ value: p, en: tEn(`prayers.${p}`), he: tHe(`prayers.${p}`) })),
    dayTypes: DAY_TYPES.map((d) => ({ value: d, en: tEn(`dayTypes.${d}`), he: tHe(`dayTypes.${d}`) })),
    timeTypes: [
      { value: 'fixed', en: tEn('admin.fixedTime'), he: tHe('admin.fixedTime') },
      { value: 'zman', en: tEn('admin.zmanBased'), he: tHe('admin.zmanBased') },
    ],
    roundDirections: (['up', 'down', 'nearest'] as const).map((d) => ({
      value: d,
      en: tEn(`admin.round${d.charAt(0).toUpperCase()}${d.slice(1)}`),
      he: tHe(`admin.round${d.charAt(0).toUpperCase()}${d.slice(1)}`),
    })),
    instructionsTitle: { en: tEn('admin.uploadSchedule'), he: tHe('admin.uploadSchedule') },
    instructionsBody: { en: tEn('info.xlsxUpload'), he: tHe('info.xlsxUpload') },
  };

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<CsvParsedRow[] | null>(null);
  const [errors, setErrors] = useState<CsvRowError[] | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetParseState = () => {
    setRows(null);
    setErrors(null);
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    const blob = await generateScheduleXlsxTemplate(labelSets, isHebrew);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shul-times-schedule-template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSuccess(false);
    const buffer = await file.arrayBuffer();
    const result = await parseScheduleXlsx(buffer, labelSets);
    setRows(result.rows);
    setErrors(result.errors);
  };

  const handleConfirm = async () => {
    if (!rows || rows.length === 0) return;
    setUploading(true);
    try {
      const merged = mergeCsvRowsIntoSchedules(schedules, rows);
      await onUpload(merged);
      setSuccess(true);
      setRows(null);
      setErrors(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  const isExisting = (row: CsvParsedRow) =>
    schedules.some((s) => scheduleMatchKey(s) === scheduleMatchKey(row.data));

  const rowPreviewKey = (row: CsvParsedRow) =>
    `${row.rowNumber}-${scheduleMatchKey(row.data)}`;

  return (
    <div className="bg-paper rounded-[16px] border border-ink/10 p-4 space-y-3">
      {/* The info icon is its own button, so it sits beside the toggle, not inside it. */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="text-sm font-medium text-ink text-start"
        >
          {t('admin.uploadSchedule')}
        </button>
        <InfoTooltip text={t('info.xlsxUpload')} />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={t('admin.uploadSchedule')}
          className="ms-auto flex-1 flex justify-end"
        >
          {open ? (
            <ChevronUp size={18} className="text-ash shrink-0" />
          ) : (
            <ChevronDown size={18} className="text-ash shrink-0" />
          )}
        </button>
      </div>

      {open && (
        <div className="space-y-3 pt-1">
          <p className="text-sm text-ash">{t('admin.xlsxMatchHint')}</p>

          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="px-4 py-2 rounded-[10px] border border-ink text-ink text-sm font-medium hover:opacity-80 transition-opacity"
          >
            {t('admin.downloadTemplate')}
          </button>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="block w-full text-sm text-ash file:me-3 file:px-4 file:py-2 file:border-0 file:bg-ink file:text-paper file:rounded-[10px] file:text-sm file:font-medium file:cursor-pointer cursor-pointer"
            />
          </div>

          {errors && errors.length > 0 && (
            <div className="rounded-[16px] border border-danger-100 bg-danger-50 p-3 space-y-1">
              <p className="text-sm font-medium text-danger-700">{t('admin.csvErrorsTitle')}</p>
              {errors.map((e) => (
                <p key={`${e.rowNumber}-${e.message}`} className="text-sm text-danger-600">
                  {e.rowNumber > 0
                    ? `${t('admin.csvRowLabel', { row: e.rowNumber })}: ${e.message}`
                    : e.message}
                </p>
              ))}
            </div>
          )}

          {rows && rows.length > 0 && errors && errors.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-ink">{t('admin.csvRowsSummary', { count: rows.length })}</p>
              <div className="overflow-x-auto rounded-[10px] border border-ink/10">
                <table className="w-full text-sm text-start">
                  <thead className="bg-fog text-ink">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('admin.csvColumnPrayer')}</th>
                      <th className="px-3 py-2 font-medium">{t('admin.csvColumnDayTypes')}</th>
                      <th className="px-3 py-2 font-medium">{t('admin.csvColumnNote')}</th>
                      <th className="px-3 py-2 font-medium">{t('admin.csvColumnAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const noteDisplay = isHebrew
                        ? row.data.noteHe || row.data.note
                        : row.data.note || row.data.noteHe;
                      return (
                        <tr key={rowPreviewKey(row)} className="border-t border-ink/10">
                          <td className="px-3 py-2 text-ink">
                            {t(`prayers.${row.data.prayerType}`)}
                          </td>
                          <td className="px-3 py-2 text-ink">
                            {row.data.dayTypes.map((d) => t(`dayTypes.${d}`)).join(', ')}
                          </td>
                          <td className="px-3 py-2 text-ink">
                            {noteDisplay || '—'}
                          </td>
                          <td className="px-3 py-2 text-ink">
                            {isExisting(row)
                              ? t('admin.csvActionUpdate')
                              : t('admin.csvActionNew')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={uploading}
                  className="px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {t('admin.confirmUpload')}
                </button>
                <button
                  type="button"
                  onClick={resetParseState}
                  className="px-4 py-2 rounded-[10px] text-sm text-ink hover:opacity-70 transition-opacity"
                >
                  {t('admin.csvChangeFile')}
                </button>
              </div>
            </div>
          )}

          {rows && rows.length === 0 && errors && errors.length === 0 && (
            <p className="text-sm text-ash">{t('admin.csvEmpty')}</p>
          )}

          {success && (
            <p className="text-sm text-ink">{t('admin.uploadSuccess')}</p>
          )}
        </div>
      )}
    </div>
  );
}
